import { validatePostalCode, validateEmail } from "@/lib/validation";
import { useState, useEffect } from "react";
import { TickSVG, DownArrow } from "../svg";
import InputGuideText from "@/components/input-guide-text";
import { ITEM_DESC_ERROR_MSG } from "@/components/create-shipment-form/item";
import useNoInitialRenderEffect from "@/hooks/useNoInitialRenderEffect";
import classNames from "classnames";
import {
  MIN_ITEM_DESCRIPTION_LENGTH,
  MAX_ITEM_DESCRIPTION_LENGTH,
} from "@/lib/constants";
import {
  textHasDisallowedCharacters,
  DISALLOWED_CHARACTERS_ERROR_MESSAGE,
} from "@/utils/languages";

export const validateInput = (type, state, setState) => {
  if (type === "float") {
    if (!state.match(/[^0-9.]/g)) {
      setState(state);
    }
  } else if (type === "number") {
    if (!state.match(/[^0-9]/g) && state != "0") {
      setState(state);
    }
  } else if (type === "mobile") {
    if (!state.match(/([^0-9()-\s])/g)) {
      setState(state);
    }
  } else {
    setState(state);
  }
};

export default function FormWithValidation({
  label,
  category,
  state,
  setState,
  isValid,
  setIsValid,
  inputref,
  country,
  isFetching = false,
  children,
  options,
  selectValue,
  selectDefault,
  selectOnChange,
  selectOnBlur,
  ...props
}) {
  const [errMsg, setErrMsg] = useState("");

  const validateWithFn = (category, value) => {
    let msg = "";
    switch (category) {
      case "name":
      case "address":
        if (!value) msg = "Required";
        else {
          msg = textHasDisallowedCharacters(value)
            ? DISALLOWED_CHARACTERS_ERROR_MESSAGE
            : "";
        }
        break;
      case "email":
        msg = validateEmail(value) ? "" : "Please enter a valid email";
        break;
      case "postal":
        msg = validatePostalCode(value, country);
        break;
      case "recipientEmail":
        if (value) {
          msg = validateEmail(value) ? "" : "Please enter a valid email";
        } else {
          setErrMsg(msg);
          return "none";
        }
        break;
      case "city":
        if (!value) msg = "Please enter a city";
        // FedEx allows max 35 chars
        else if (value.length < 4 || value.length > 35)
          msg = "City must be 4-35 characters long";
        break;
      case "item-description":
        if (!value) msg = "Please enter a description";
        // TODO: review why this check repeats multiple times in the codebase
        // form-container.js has it, package.js has it, and here
        else if (
          !!value &&
          !(
            value.trim().length >= MIN_ITEM_DESCRIPTION_LENGTH &&
            value.trim().length <= MAX_ITEM_DESCRIPTION_LENGTH
          )
        )
          msg = ITEM_DESC_ERROR_MSG;
        break;
      case "float":
        if (!parseFloat(value) > 0) msg = "Must be greater than 0";
        break;
      default:
        if (value.length === 0) msg = "Required";
    }
    setErrMsg(msg);
    return msg === "";
  };

  useEffect(() => {
    if ((state !== "" || !isValid) && setState) {
      setIsValid(validateWithFn(category, state));
    }
  }, []);

  useNoInitialRenderEffect(() => {
    if (category === "postal" && state !== "") {
      setIsValid(validateWithFn(category, state));
    }
  }, [country]);

  useNoInitialRenderEffect(() => {
    if (setState) {
      if (!isValid) {
        validateWithFn(category, state, false);
      }
    } else if (options) {
      if (selectValue !== "") {
        setIsValid(true);
      } else setErrMsg("Required");
    }
  }, [isValid, state, selectValue]);

  return (
    <div className="w-full flex flex-col group">
      <label
        htmlFor={props.id}
        className={classNames(
          "font-normal text-subheading-gray w-max",
          "group-focus-within:text-dark-purple"
        )}
      >
        {label}
      </label>
      <div
        id={props.id + "container"}
        className={classNames(
          "mt-1 mb-6 relative border rounded-xl",
          "flex items-center h-10 focus-within:border-light-purple focus:shadow-border-focus",
          isValid !== "none" && !isValid
            ? "border-error-red shadow-border-error"
            : "border-border-dark-gray shadow-border"
        )}
      >
        {setState ? (
          <>
            <div
              className={`relative flex items-center h-full z-[2] ${
                options ? "w-[calc(100%-70px)]" : "w-full"
              }`}
            >
              <input
                className={classNames(
                  "focus:outline-none pl-3 w-full h-full rounded-xl",
                  !options && "pr-8"
                )}
                ref={inputref}
                maxLength={80}
                value={state}
                disabled={isFetching}
                onBlur={(e) => {
                  setIsValid(validateWithFn(category, e.target.value));
                  validateInput(category, e.target.value, setState);
                }}
                onChange={(e) =>
                  validateInput(category, e.target.value, setState)
                }
                {...props}
              ></input>
            </div>
          </>
        ) : null}
        {isValid === true && (
          <TickSVG
            className={`
                absolute right-0 mr-2
              w-[18px] group-focus-within:invisible stroke-dark-purple z-[2]`}
          />
        )}
        {options ? (
          <>
            <div className="absolute h-full w-full flex items-center top-0 z-[1]">
              <select
                id={setState ? "" : props.id}
                className="focus:outline-none w-full h-full rounded-xl appearance-none bg-transparent hover:cursor-pointer pl-3"
                value={selectValue ?? ""}
                onChange={selectOnChange}
                onBlur={selectOnBlur}
                tabIndex={setState ? "-1" : 0}
              >
                <option value="" disabled>
                  {selectDefault}
                </option>
                {options}
              </select>
            </div>
            <div className="absolute right-0 pr-[1.6rem] h-full flex items-center pointer-events-none z-[2] group-focus-within:invisible">
              <DownArrow
                color="#373F41"
                className="w-[14px] h-[9px] mt-1 mr-2.5"
              />
              <hr className="h-2/3 self-center border-l border-border-gray group-focus-within:border-light-purple mr-2" />
            </div>
          </>
        ) : null}
        <InputGuideText
          className={`absolute top-10 mt-0.5 ml-0.5 ${
            isValid === false ? "" : "hidden"
          }`}
          isWarning={true}
        >
          {errMsg}
        </InputGuideText>
      </div>
    </div>
  );
}
